import type { components } from '@/types/apiSchema';

export type CompanySizeBand = Exclude<
  components['schemas']['EmployerSizeBand'],
  'Any'
>;
export type CoresignalCompanyApi =
  components['schemas']['CoresignalCompanyResponse'];
export type CompanyProfileRequestApi =
  components['schemas']['CompanyProfileRequest'];

/** The bands a company's own size can be in: the job form's employer size choices, without "Any size". */
export const companySizeOptions: { value: CompanySizeBand; label: string }[] = [
  { value: 'Small', label: 'Up to 200 people' },
  { value: 'Medium', label: '201 to 1,000 people' },
  { value: 'Large', label: 'Over 1,000 people' },
];

/** The band a sign-up size already gives: 1 to 10, 10 to 50 and 50 to 100 people are all up to 200. */
export const bandFromSignUpSize = (
  size: string | null | undefined,
): CompanySizeBand | undefined =>
  size === '1-10' || size === '10-50' || size === '50-100'
    ? 'Small'
    : undefined;

/** How many people Coresignal says the company has, in words; undefined when it doesn't say. */
export const headcountLabel = (
  company: Pick<CoresignalCompanyApi, 'employeesCount' | 'sizeRange'>,
): string | undefined => {
  const count = company.employeesCount;
  if (count) {
    return `About ${count.toLocaleString('en-US')} ${count === 1 ? 'person' : 'people'}`;
  }
  return company.sizeRange ?? undefined;
};

/** What the user has said so far, answering "Is this your company?". */
export interface ProfileCheckDraft {
  /** Whether the looked-up company is theirs; false also when nothing was found. */
  isOurCompany: boolean;
  /** Picked because Coresignal's headcount is wrong or missing, or the company isn't theirs. */
  sizeBand?: CompanySizeBand;
  /** Picked because Coresignal's industry is wrong or missing, or the company isn't theirs. */
  industry?: string;
}

/**
 * What still needs picking: on a no, or with nothing found, both the size and the industry; on a yes, whatever the
 * user marked wrong, or Coresignal doesn't have.
 */
export const neededPicks = (
  draft: ProfileCheckDraft,
  found: CoresignalCompanyApi | null | undefined,
  marked: { size: boolean; industry: boolean },
): { size: boolean; industry: boolean } =>
  draft.isOurCompany && found
    ? {
        size: marked.size || !found.employeesCount,
        industry: marked.industry || !found.industry,
      }
    : { size: true, industry: true };

/** Whether every needed pick is made. */
export const canAnswer = (
  draft: ProfileCheckDraft,
  needed: { size: boolean; industry: boolean },
): boolean =>
  (!needed.size || Boolean(draft.sizeBand)) &&
  (!needed.industry || Boolean(draft.industry));

/** The answer finlay-api keeps: the picks go only where they were needed, so a yes keeps the rest of Coresignal's. */
export const profileAnswerRequest = (
  draft: ProfileCheckDraft,
  needed: { size: boolean; industry: boolean },
): CompanyProfileRequestApi => ({
  isOurCompany: draft.isOurCompany,
  sizeBand: needed.size ? (draft.sizeBand ?? null) : null,
  industry: needed.industry ? (draft.industry ?? null) : null,
});
